/**
 * cart-free-gift.js
 * Intercepts the checkout process to append the actual free gift variant
 * to the cart only when proceeding to checkout.
 */

document.addEventListener('DOMContentLoaded', () => {
    // 1. Cleanup any real free gifts stuck in the cart (e.g. if user hits back button from checkout)
    cleanupStuckGifts();

    // Back from checkout usually restores this page from bfcache: DOMContentLoaded doesn't
    // fire again and the DOM is the pre-checkout snapshot, while the server cart now holds
    // the real gift (prepended, shifting every line index). Clean up and re-sync the UI.
    window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        restoreCheckoutButtons();
        cleanupStuckGifts({ forceRefresh: true });
    });

    // 2. We attach a capture-phase listener to intercept checkout button clicks
    // before any other script can stop propagation or submit the form.
    document.addEventListener('click', async (e) => {
        const target = e.target.closest('button[name="checkout"], input[name="checkout"], [href="/checkout"]');
        if (!target) return;

        const dummyGifts = document.querySelectorAll('.cart-items__table-row--dummy-gift[data-dummy-gift-id]');
        if (dummyGifts.length === 0) return;

        e.preventDefault();
        e.stopPropagation();

        if (!target.dataset.originalHtml) target.dataset.originalHtml = target.innerHTML;
        target.disabled = true;
        
        const loaderHTML = `
            <div class="loading-overlay__spinner">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
            </div>`;
        if (target.innerHTML) {
            target.innerHTML = loaderHTML;
        }

        await processFreeGifts(dummyGifts);
    }, true); // Use capture phase

    // Also intercept form submits just in case
    document.addEventListener('submit', async (e) => {
        if (e.target.matches('form[action="/cart"], form.cart-form, form#cart-form')) {
            const isCheckout = e.submitter && e.submitter.name === 'checkout';
            if (!isCheckout) return;

            const dummyGifts = document.querySelectorAll('.cart-items__table-row--dummy-gift[data-dummy-gift-id]');
            if (dummyGifts.length === 0) return;

            e.preventDefault();
            e.stopPropagation();

            if (e.submitter) e.submitter.disabled = true;
            await processFreeGifts(dummyGifts);
        }
    }, true);

    async function processFreeGifts(dummyGifts) {
        const itemsToAdd = [];
        const addedIds = new Set();
        
        dummyGifts.forEach(gift => {
            const variantId = gift.getAttribute('data-dummy-gift-id');
            if (variantId && !addedIds.has(variantId)) {
                itemsToAdd.push({
                    id: parseInt(variantId, 10),
                    quantity: 1,
                    properties: {
                        _is_free_gift: 'true'
                    }
                });
                addedIds.add(variantId);
            }
        });

        try {
            const cartReq = await fetch(window.Shopify.routes.root + 'cart.js');
            const cartData = await cartReq.json();
            
            const existingItems = new Set(cartData.items.filter(i => getCartItemProperty(i, '_is_free_gift')).map(i => i.variant_id.toString()));
            const newItemsToAdd = itemsToAdd.filter(item => !existingItems.has(item.id.toString()));

            if (newItemsToAdd.length > 0) {
                await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: newItemsToAdd })
                });
            }
            
            window.location.href = window.Shopify.routes.root + 'checkout';
        } catch (error) {
            console.error('Failed to add free gift before checkout:', error);
            window.location.href = window.Shopify.routes.root + 'checkout';
        }
    }
    
    // Undo the loading state processFreeGifts() put on the checkout button, which
    // would otherwise stay spinning/disabled in the bfcache snapshot.
    function restoreCheckoutButtons() {
        document.querySelectorAll('[data-original-html]').forEach((button) => {
            button.innerHTML = button.dataset.originalHtml;
            button.disabled = false;
            delete button.dataset.originalHtml;
        });
    }

    // Re-render the cart sections and header count from the server cart.
    function refreshCartUI() {
        document.querySelectorAll('cart-items-component').forEach((component) => component.refresh?.());
        document.querySelectorAll('cart-icon').forEach((icon) => icon.syncFromServer?.());
    }

    function getCartItemProperty(item, propName) {
        if (!item.properties) return null;
        if (Array.isArray(item.properties)) {
            const prop = item.properties.find(
                (p) => p[0] === propName || p.name === propName
            );
            return prop ? prop[1] || prop.value : null;
        }
        return item.properties[propName];
    }

    async function cleanupStuckGifts({ forceRefresh = false } = {}) {
        try {
            const cartReq = await fetch(window.Shopify.routes.root + 'cart.js');
            const cartData = await cartReq.json();
            
            const giftsToRemove = {};
            cartData.items.forEach(item => {
                if (getCartItemProperty(item, '_is_free_gift')) {
                    giftsToRemove[item.key] = 0;
                }
            });

            if (Object.keys(giftsToRemove).length > 0) {
                await fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates: giftsToRemove })
                });
                
                // The rendered markup still reflects the gift lines (indexes, counts), so re-sync.
                forceRefresh = true;
            }
        } catch (error) {
            console.error('Failed to cleanup stuck free gifts:', error);
        }

        if (forceRefresh) refreshCartUI();
    }
});
